import { useState, useEffect, useCallback } from 'react';
import { useLocation } from 'react-router-dom';
import { api } from '../../services/api';
import {
  ListTodo, Plus, Check, X, Loader2, Clock,
  AlertTriangle, RefreshCw, ChevronDown, ChevronUp, Copy,
  Pause, Trash2, Pencil, CircleDot, Play,
} from 'lucide-react';

interface WorkQueueItem {
  request_id: string;
  created_at: string;
  status: string;
  target_area: string;
  target_component: string;
  request_text: string;
  completed_at: string;
  result_notes: string;
  created_by: string;
}

const TARGET_AREAS = ['webapp', 'webapp - mobile render', 'backend', 'generation', 'admin', 'infrastructure', 'webpage'];

const STATUS_STYLES: Record<string, { bg: string; text: string; border: string }> = {
  todo: { bg: 'bg-purple-50', text: 'text-purple-700', border: 'border-purple-200' },
  queued: { bg: 'bg-yellow-50', text: 'text-yellow-700', border: 'border-yellow-200' },
  paused: { bg: 'bg-orange-50', text: 'text-orange-700', border: 'border-orange-200' },
  processing: { bg: 'bg-blue-50', text: 'text-blue-700', border: 'border-blue-200' },
  complete: { bg: 'bg-green-50', text: 'text-green-700', border: 'border-green-200' },
  failed: { bg: 'bg-red-50', text: 'text-red-700', border: 'border-red-200' },
};

function StatusBadge({ status }: { status: string }) {
  const style = STATUS_STYLES[status] || STATUS_STYLES.queued;
  return (
    <span className={`inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-xs font-medium ${style.bg} ${style.text} border ${style.border}`}>
      {status === 'todo' && <CircleDot className="w-3 h-3" />}
      {status === 'queued' && <Clock className="w-3 h-3" />}
      {status === 'paused' && <Pause className="w-3 h-3" />}
      {status === 'processing' && <Loader2 className="w-3 h-3 animate-spin" />}
      {status === 'complete' && <Check className="w-3 h-3" />}
      {status === 'failed' && <AlertTriangle className="w-3 h-3" />}
      {status}
    </span>
  );
}

function formatDate(isoStr: string): string {
  if (!isoStr) return '--';
  try {
    const d = new Date(isoStr);
    return d.toLocaleString('en-US', {
      month: 'short',
      day: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
      hour12: true,
    });
  } catch {
    return isoStr;
  }
}

export function WorkQueue() {
  const location = useLocation();
  const [items, setItems] = useState<WorkQueueItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Status filter state
  const [statusFilter, setStatusFilter] = useState<string | null>(null);

  // New request form state
  const [showForm, setShowForm] = useState(false);
  const [targetArea, setTargetArea] = useState('webapp');
  const [targetComponent, setTargetComponent] = useState('');
  const [requestText, setRequestText] = useState('');
  const [submitting, setSubmitting] = useState(false);

  // Expanded items
  const [expandedIds, setExpandedIds] = useState<Set<string>>(new Set());

  // Updating items
  const [updatingId, setUpdatingId] = useState<string | null>(null);

  // Notes editing
  const [editingNotesId, setEditingNotesId] = useState<string | null>(null);
  const [editingNotesValue, setEditingNotesValue] = useState('');

  // Pause-and-edit state
  const [editingItemId, setEditingItemId] = useState<string | null>(null);
  const [editOriginalStatus, setEditOriginalStatus] = useState<string>('queued');
  const [editTargetArea, setEditTargetArea] = useState('');
  const [editTargetComponent, setEditTargetComponent] = useState('');
  const [editRequestText, setEditRequestText] = useState('');

  // Delete confirmation
  const [confirmDeleteId, setConfirmDeleteId] = useState<string | null>(null);


  const loadData = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const result = await api.listWorkQueue();
      setItems(result.items || []);
    } catch (err: unknown) {
      const axiosErr = err as { response?: { data?: { message?: string } } };
      setError(axiosErr?.response?.data?.message || 'Failed to load work queue');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { loadData(); }, [loadData]);

  // Handle prefill from LogViewer navigation
  useEffect(() => {
    const state = location.state as { prefill?: boolean; target_area?: string; target_component?: string; request_text?: string } | null;
    if (state?.prefill) {
      setTargetArea(state.target_area || 'backend');
      setTargetComponent(state.target_component || '');
      setRequestText(state.request_text || '');
      setShowForm(true);
      // Clear the state so refreshing doesn't re-prefill
      window.history.replaceState({}, document.title);
    }
  }, [location.state]);

  const handleSubmit = async (e: React.FormEvent, status: 'queued' | 'todo' = 'queued') => {
    e.preventDefault();
    if (!requestText.trim() || !targetArea) return;

    setSubmitting(true);
    setError(null);
    try {
      await api.createWorkQueueItem({
        target_area: targetArea,
        target_component: targetComponent.trim(),
        request_text: requestText.trim(),
        status,
      });
      setTargetComponent('');
      setRequestText('');
      setShowForm(false);
      await loadData();
    } catch (err: unknown) {
      const axiosErr = err as { response?: { data?: { message?: string } } };
      setError(axiosErr?.response?.data?.message || 'Failed to create work queue item');
    } finally {
      setSubmitting(false);
    }
  };

  const handlePauseAndEdit = async (item: WorkQueueItem) => {
    setUpdatingId(item.request_id);
    setError(null);
    try {
      await api.updateWorkQueueItem(item.request_id, { status: 'paused' });
      setEditingItemId(item.request_id);
      setEditOriginalStatus(item.status);
      setEditTargetArea(item.target_area);
      setEditTargetComponent(item.target_component || '');
      setEditRequestText(item.request_text);
      // Ensure the item is expanded so the edit form is visible
      setExpandedIds((prev) => new Set(prev).add(item.request_id));
      await loadData();
    } catch (err: unknown) {
      const axiosErr = err as { response?: { data?: { message?: string } } };
      setError(axiosErr?.response?.data?.message || 'Failed to pause item');
    } finally {
      setUpdatingId(null);
    }
  };

  const handleSaveAndRequeue = async (requestId: string, newStatus: 'queued' | 'todo' = 'queued') => {
    if (!editRequestText.trim()) return;
    setUpdatingId(requestId);
    setError(null);
    try {
      await api.updateWorkQueueItem(requestId, {
        status: newStatus,
        target_area: editTargetArea,
        target_component: editTargetComponent.trim(),
        request_text: editRequestText.trim(),
      });
      setEditingItemId(null);
      await loadData();
    } catch (err: unknown) {
      const axiosErr = err as { response?: { data?: { message?: string } } };
      setError(axiosErr?.response?.data?.message || 'Failed to save item');
    } finally {
      setUpdatingId(null);
    }
  };

  const handleCancelEdit = async (requestId: string) => {
    setUpdatingId(requestId);
    setError(null);
    try {
      await api.updateWorkQueueItem(requestId, { status: editOriginalStatus === 'todo' ? 'todo' : 'queued' });
      setEditingItemId(null);
      await loadData();
    } catch (err: unknown) {
      const axiosErr = err as { response?: { data?: { message?: string } } };
      setError(axiosErr?.response?.data?.message || 'Failed to re-queue item');
    } finally {
      setUpdatingId(null);
    }
  };

  const handleDelete = async (requestId: string) => {
    setUpdatingId(requestId);
    setError(null);
    try {
      await api.deleteWorkQueueItem(requestId);
      setConfirmDeleteId(null);
      if (editingItemId === requestId) setEditingItemId(null);
      await loadData();
    } catch (err: unknown) {
      const axiosErr = err as { response?: { data?: { message?: string } } };
      setError(axiosErr?.response?.data?.message || 'Failed to delete item');
    } finally {
      setUpdatingId(null);
    }
  };

  const handleNotesSave = async (requestId: string) => {
    setUpdatingId(requestId);
    setError(null);
    try {
      await api.updateWorkQueueItem(requestId, { result_notes: editingNotesValue });
      setEditingNotesId(null);
      setEditingNotesValue('');
      await loadData();
    } catch (err: unknown) {
      const axiosErr = err as { response?: { data?: { message?: string } } };
      setError(axiosErr?.response?.data?.message || 'Failed to save notes');
    } finally {
      setUpdatingId(null);
    }
  };

  const handleQueueTodo = async (requestId: string) => {
    setUpdatingId(requestId);
    setError(null);
    try {
      await api.updateWorkQueueItem(requestId, { status: 'queued' });
      await loadData();
    } catch (err: unknown) {
      const axiosErr = err as { response?: { data?: { message?: string } } };
      setError(axiosErr?.response?.data?.message || 'Failed to queue item');
    } finally {
      setUpdatingId(null);
    }
  };

  const handleClone = (item: WorkQueueItem) => {
    setTargetArea(item.target_area);
    setTargetComponent(item.target_component || '');
    setRequestText(item.request_text);
    setShowForm(true);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const toggleExpand = (id: string) => {
    setExpandedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  // Summary stats
  const stats = {
    total: items.length,
    todo: items.filter((i) => i.status === 'todo').length,
    queued: items.filter((i) => i.status === 'queued').length,
    paused: items.filter((i) => i.status === 'paused').length,
    processing: items.filter((i) => i.status === 'processing').length,
    complete: items.filter((i) => i.status === 'complete').length,
    failed: items.filter((i) => i.status === 'failed').length,
  };

  // Filtered items based on status filter
  const filteredItems = statusFilter
    ? items.filter((i) => i.status === statusFilter)
    : items;

  return (
    <div className="p-6 max-w-7xl mx-auto">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-3">
          <ListTodo className="w-7 h-7 text-indigo-500" />
          <h1 className="text-2xl font-bold text-gray-800">AI Work Queue</h1>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={() => setShowForm(!showForm)}
            className="flex items-center gap-2 px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition-colors"
          >
            <Plus className="w-4 h-4" />
            New Request
          </button>
          <button
            onClick={loadData}
            disabled={loading}
            className="flex items-center gap-2 px-4 py-2 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50"
          >
            <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
            Refresh
          </button>
        </div>
      </div>

      {/* Error */}
      {error && (
        <div className="mb-4 p-3 bg-red-50 text-red-700 rounded-lg flex items-center justify-between">
          <div className="flex items-center gap-2">
            <AlertTriangle className="w-4 h-4" />
            <span>{error}</span>
          </div>
          <button onClick={() => setError(null)} className="text-red-400 hover:text-red-600">
            <X className="w-4 h-4" />
          </button>
        </div>
      )}

      {/* Summary Stats */}
      <div className="grid grid-cols-7 gap-4 mb-6">
        {[
          { label: 'Total', value: stats.total, color: 'text-gray-800', bg: 'bg-gray-50', filterKey: null as string | null },
          { label: 'To Do', value: stats.todo, color: 'text-purple-700', bg: 'bg-purple-50', filterKey: 'todo' },
          { label: 'Queued', value: stats.queued, color: 'text-yellow-700', bg: 'bg-yellow-50', filterKey: 'queued' },
          { label: 'Paused', value: stats.paused, color: 'text-orange-700', bg: 'bg-orange-50', filterKey: 'paused' },
          { label: 'Processing', value: stats.processing, color: 'text-blue-700', bg: 'bg-blue-50', filterKey: 'processing' },
          { label: 'Complete', value: stats.complete, color: 'text-green-700', bg: 'bg-green-50', filterKey: 'complete' },
          { label: 'Failed', value: stats.failed, color: 'text-red-700', bg: 'bg-red-50', filterKey: 'failed' },
        ].map(({ label, value, color, bg, filterKey }) => (
          <div
            key={label}
            onClick={() => setStatusFilter(statusFilter === filterKey ? null : filterKey)}
            className={`${bg} rounded-xl border p-4 text-center cursor-pointer transition-all hover:shadow-md ${
              statusFilter === filterKey ? 'border-indigo-400 ring-2 ring-indigo-200' : 'border-gray-200'
            }`}
          >
            <div className={`text-2xl font-bold ${color}`}>{value}</div>
            <div className="text-xs text-gray-500 mt-1">{label}</div>
          </div>
        ))}
      </div>

      {/* New Request Form */}
      {showForm && (
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 mb-6">
          <div className="p-5 border-b border-gray-100">
            <h2 className="text-lg font-semibold text-gray-800">New Work Request</h2>
          </div>
          <form onSubmit={handleSubmit} className="p-5 space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Target Area</label>
                <select
                  value={targetArea}
                  onChange={(e) => setTargetArea(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                >
                  {TARGET_AREAS.map((area) => (
                    <option key={area} value={area}>{area}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Target Component</label>
                <input
                  type="text"
                  value={targetComponent}
                  onChange={(e) => setTargetComponent(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                  placeholder="e.g., JobCard, ResumeEditor, subcomponent.py"
                />
              </div>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Request Description</label>
              <textarea
                value={requestText}
                onChange={(e) => setRequestText(e.target.value)}
                rows={4}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 resize-y"
                placeholder="Describe the work to be done..."
                required
              />
            </div>
            <div className="flex items-center justify-end gap-3">
              <button
                type="button"
                onClick={() => setShowForm(false)}
                className="px-4 py-2 text-sm text-gray-600 hover:text-gray-800 transition-colors"
              >
                Cancel
              </button>
              <button
                type="button"
                disabled={submitting || !requestText.trim()}
                onClick={(e) => handleSubmit(e as unknown as React.FormEvent, 'todo')}
                className="flex items-center gap-2 px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 disabled:opacity-50 transition-colors"
              >
                {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : <CircleDot className="w-4 h-4" />}
                Save as To Do
              </button>
              <button
                type="submit"
                disabled={submitting || !requestText.trim()}
                className="flex items-center gap-2 px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 disabled:opacity-50 transition-colors"
              >
                {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : <Plus className="w-4 h-4" />}
                Create & Queue
              </button>
            </div>
            <div className="mt-3 flex items-start gap-2 p-3 bg-blue-50 border border-blue-200 rounded-lg">
              <svg className="w-4 h-4 text-blue-500 mt-0.5 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
              </svg>
              <p className="text-xs text-blue-700">Your request will be picked up on the next development interval. Changes may take up to an hour to appear on the site.</p>
            </div>
          </form>
        </div>
      )}

      {/* Loading */}
      {loading && items.length === 0 ? (
        <div className="flex items-center justify-center py-20">
          <Loader2 className="w-8 h-8 animate-spin text-gray-400" />
        </div>
      ) : items.length === 0 ? (
        <div className="text-center py-20">
          <ListTodo className="w-16 h-16 text-gray-300 mx-auto mb-4" />
          <h2 className="text-xl font-semibold text-gray-600 mb-2">No Work Queue Items</h2>
          <p className="text-gray-500">Click "New Request" to create your first work queue item.</p>
        </div>
      ) : (
        /* Work Queue Items List */
        <div className="bg-white rounded-xl shadow-sm border border-gray-200">
          <div className="p-5 border-b border-gray-100 flex items-center justify-between">
            <h2 className="text-lg font-semibold text-gray-800">
              {statusFilter
                ? `${statusFilter.charAt(0).toUpperCase() + statusFilter.slice(1)} Requests (${filteredItems.length})`
                : `All Requests (${items.length})`}
            </h2>
            {statusFilter && (
              <button
                onClick={() => setStatusFilter(null)}
                className="flex items-center gap-1 px-3 py-1.5 text-xs font-medium text-gray-600 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors"
              >
                <X className="w-3 h-3" />
                Clear Filter
              </button>
            )}
          </div>
          <div className="divide-y divide-gray-100">
            {filteredItems.map((item) => {
              const isExpanded = expandedIds.has(item.request_id);
              const isUpdating = updatingId === item.request_id;
              const isEditing = editingItemId === item.request_id;
              const isConfirmingDelete = confirmDeleteId === item.request_id;
              const canPauseEdit = item.status === 'queued' || item.status === 'todo';
              const canDelete = item.status === 'queued' || item.status === 'paused' || item.status === 'failed' || item.status === 'todo';
              const canQueue = item.status === 'todo';

              return (
                <div key={item.request_id} className="hover:bg-gray-50/50 transition-colors">
                  {/* Row summary */}
                  <div
                    className="px-5 py-4 flex items-center gap-4 cursor-pointer"
                    onClick={() => toggleExpand(item.request_id)}
                  >
                    {/* Expand icon */}
                    <div className="text-gray-400">
                      {isExpanded ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
                    </div>

                    {/* Status */}
                    <div className="w-28 shrink-0">
                      <StatusBadge status={item.status} />
                    </div>

                    {/* Target area + component */}
                    <div className="w-48 shrink-0">
                      <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-gray-100 text-gray-700">
                        {item.target_area}
                      </span>
                      {item.target_component && (
                        <span className="ml-2 text-xs text-gray-500">{item.target_component}</span>
                      )}
                    </div>

                    {/* Request text (truncated) */}
                    <div className="flex-1 min-w-0">
                      <p className="text-sm text-gray-800 truncate">{item.request_text}</p>
                    </div>

                    {/* Created date */}
                    <div className="text-xs text-gray-400 shrink-0 w-32 text-right">
                      {formatDate(item.created_at)}
                    </div>

                    {/* Action buttons */}
                    {(canPauseEdit || canDelete || canQueue) && (
                      <div className="flex items-center gap-2 shrink-0" onClick={(e) => e.stopPropagation()}>
                        {canQueue && (
                          <button
                            onClick={() => handleQueueTodo(item.request_id)}
                            disabled={isUpdating}
                            className="flex items-center gap-1 px-3 py-1.5 text-xs font-medium text-yellow-700 bg-yellow-50 rounded-lg hover:bg-yellow-100 disabled:opacity-50 transition-colors"
                            title="Move to queue for processing"
                          >
                            {isUpdating ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Play className="w-3.5 h-3.5" />}
                            Queue
                          </button>
                        )}
                        {canPauseEdit && (
                          <button
                            onClick={() => handlePauseAndEdit(item)}
                            disabled={isUpdating}
                            className="flex items-center gap-1 px-3 py-1.5 text-xs font-medium text-orange-700 bg-orange-50 rounded-lg hover:bg-orange-100 disabled:opacity-50 transition-colors"
                            title="Pause and edit this request"
                          >
                            {isUpdating ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Pencil className="w-3.5 h-3.5" />}
                            Pause & Edit
                          </button>
                        )}
                        {isConfirmingDelete ? (
                          <>
                            <button
                              onClick={() => handleDelete(item.request_id)}
                              disabled={isUpdating}
                              className="flex items-center gap-1 px-3 py-1.5 text-xs font-medium text-white bg-red-600 rounded-lg hover:bg-red-700 disabled:opacity-50 transition-colors"
                            >
                              {isUpdating ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Trash2 className="w-3.5 h-3.5" />}
                              Confirm
                            </button>
                            <button
                              onClick={() => setConfirmDeleteId(null)}
                              className="px-3 py-1.5 text-xs font-medium text-gray-600 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors"
                            >
                              Cancel
                            </button>
                          </>
                        ) : (
                          canDelete && (
                            <button
                              onClick={() => setConfirmDeleteId(item.request_id)}
                              disabled={isUpdating}
                              className="flex items-center gap-1 px-3 py-1.5 text-xs font-medium text-red-700 bg-red-50 rounded-lg hover:bg-red-100 disabled:opacity-50 transition-colors"
                              title="Delete this request"
                            >
                              <Trash2 className="w-3.5 h-3.5" />
                              Delete
                            </button>
                          )
                        )}
                      </div>
                    )}
                  </div>

                  {/* Expanded detail */}
                  {isExpanded && (
                    <div className="px-5 pb-4 pl-14 space-y-3">
                      {/* Inline edit form for paused items */}
                      {isEditing && item.status === 'paused' ? (
                        <div className="bg-orange-50 border border-orange-200 rounded-lg p-4 space-y-3">
                          <div className="flex items-center gap-2 mb-2">
                            <Pencil className="w-4 h-4 text-orange-600" />
                            <span className="text-sm font-semibold text-orange-800">Editing Paused Request</span>
                          </div>
                          <div className="grid grid-cols-2 gap-4">
                            <div>
                              <label className="block text-sm font-medium text-gray-700 mb-1">Target Area</label>
                              <select
                                value={editTargetArea}
                                onChange={(e) => setEditTargetArea(e.target.value)}
                                className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                              >
                                {TARGET_AREAS.map((area) => (
                                  <option key={area} value={area}>{area}</option>
                                ))}
                              </select>
                            </div>
                            <div>
                              <label className="block text-sm font-medium text-gray-700 mb-1">Target Component</label>
                              <input
                                type="text"
                                value={editTargetComponent}
                                onChange={(e) => setEditTargetComponent(e.target.value)}
                                className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                                placeholder="e.g., JobCard, ResumeEditor"
                              />
                            </div>
                          </div>
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">Request Description</label>
                            <textarea
                              value={editRequestText}
                              onChange={(e) => setEditRequestText(e.target.value)}
                              rows={4}
                              className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 resize-y"
                            />
                          </div>
                          <div className="flex items-center gap-3">
                            <button
                              onClick={() => handleSaveAndRequeue(item.request_id, 'todo')}
                              disabled={isUpdating || !editRequestText.trim()}
                              className="flex items-center gap-2 px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 disabled:opacity-50 transition-colors text-sm font-medium"
                            >
                              {isUpdating ? <Loader2 className="w-4 h-4 animate-spin" /> : <CircleDot className="w-4 h-4" />}
                              Save as To Do
                            </button>
                            <button
                              onClick={() => handleSaveAndRequeue(item.request_id, 'queued')}
                              disabled={isUpdating || !editRequestText.trim()}
                              className="flex items-center gap-2 px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 disabled:opacity-50 transition-colors text-sm font-medium"
                            >
                              {isUpdating ? <Loader2 className="w-4 h-4 animate-spin" /> : <Check className="w-4 h-4" />}
                              Save & Queue
                            </button>
                            <button
                              onClick={() => handleCancelEdit(item.request_id)}
                              disabled={isUpdating}
                              className="px-4 py-2 text-sm text-gray-600 hover:text-gray-800 transition-colors disabled:opacity-50"
                            >
                              Cancel
                            </button>
                          </div>
                        </div>
                      ) : (
                        <>
                          {/* Full request text */}
                          <div>
                            <label className="text-xs font-semibold text-gray-500 uppercase">Request</label>
                            <p className="mt-1 text-sm text-gray-700 whitespace-pre-wrap bg-gray-50 rounded-lg p-3">
                              {item.request_text}
                            </p>
                          </div>

                          {/* Metadata row */}
                          <div className="flex items-center gap-6 text-xs text-gray-500">
                            <span>ID: <code className="bg-gray-100 px-1.5 py-0.5 rounded">{item.request_id}</code></span>
                            {item.created_by && <span>Created by: <code className="bg-gray-100 px-1.5 py-0.5 rounded">{item.created_by.substring(0, 8)}...</code></span>}
                            {item.completed_at && <span>Completed: {formatDate(item.completed_at)}</span>}
                          </div>

                          {/* Result notes */}
                          <div>
                            <div className="flex items-center justify-between">
                              <label className="text-xs font-semibold text-gray-500 uppercase">Result Notes</label>
                              {editingNotesId !== item.request_id && (
                                <button
                                  onClick={() => {
                                    setEditingNotesId(item.request_id);
                                    setEditingNotesValue(item.result_notes || '');
                                  }}
                                  className="text-xs text-indigo-600 hover:text-indigo-800"
                                >
                                  Edit
                                </button>
                              )}
                            </div>
                            {editingNotesId === item.request_id ? (
                              <div className="mt-1 space-y-2">
                                <textarea
                                  value={editingNotesValue}
                                  onChange={(e) => setEditingNotesValue(e.target.value)}
                                  rows={3}
                                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 resize-y"
                                  placeholder="Add notes about the result..."
                                />
                                <div className="flex items-center gap-2">
                                  <button
                                    onClick={() => handleNotesSave(item.request_id)}
                                    disabled={isUpdating}
                                    className="flex items-center gap-1 px-3 py-1.5 text-xs font-medium bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 disabled:opacity-50"
                                  >
                                    {isUpdating ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Check className="w-3.5 h-3.5" />}
                                    Save
                                  </button>
                                  <button
                                    onClick={() => { setEditingNotesId(null); setEditingNotesValue(''); }}
                                    className="px-3 py-1.5 text-xs text-gray-600 hover:text-gray-800"
                                  >
                                    Cancel
                                  </button>
                                </div>
                              </div>
                            ) : (
                              <p className="mt-1 text-sm text-gray-600 whitespace-pre-wrap">
                                {item.result_notes || <span className="text-gray-400 italic">No notes yet</span>}
                              </p>
                            )}
                          </div>

                          {/* Clone to New Request */}
                          <div className="pt-2 border-t border-gray-100">
                            <button
                              onClick={() => handleClone(item)}
                              className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-indigo-700 bg-indigo-50 rounded-lg hover:bg-indigo-100 transition-colors"
                            >
                              <Copy className="w-4 h-4" />
                              Clone to New Request
                            </button>
                          </div>
                        </>
                      )}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}
